/**
 * Phase 2.3: Display presets for large hero/display text
 */
export interface TextStylePreset {
  fontSize?: number | string;
  fontWeight?: number | string;
  lineHeight?: number | string;
  letterSpacing?: string;
  textTransform?: string;
  fontStyle?: string;
  fontFamily?: string;
  color?: string;
}

export interface TextPresetResult {
  properties: Omit<TextStylePreset, 'color'>;
  style?: Pick<TextStylePreset, 'color'>;
}

/**
 * Extended CSS named colors (50+ colors)
 * Includes all common CSS color names for maximum compatibility
 */
export const EXTENDED_CSS_COLORS: string[] = [
  // Basic colors
  'red', 'blue', 'green', 'yellow', 'orange', 'purple', 'pink',
  'black', 'white', 'gray', 'grey', 'brown', 'cyan', 'magenta',
  // Extended colors
  'navy', 'indigo', 'teal', 'coral', 'salmon', 'crimson', 'maroon',
  'olive', 'lime', 'aqua', 'turquoise', 'violet', 'plum', 'gold',
  'silver', 'beige', 'ivory', 'lavender', 'mint', 'peach', 'tan', 'sienna',
  // Additional colors
  'azure', 'chocolate', 'tomato', 'khaki', 'orchid', 'peru',
  'thistle', 'wheat', 'linen', 'snow', 'honeydew', 'seashell',
  'firebrick', 'darkred', 'darkorange', 'darkgreen', 'darkblue',
  'lightblue', 'lightgreen', 'lightgray', 'lightgrey', 'lightpink',
  'skyblue', 'steelblue', 'slategray', 'slategrey', 'royalblue',
  'midnightblue', 'forestgreen', 'limegreen', 'springgreen', 'seagreen',
  'mediumseagreen', 'darkseagreen', 'palegreen', 'mediumaquamarine',
  'cadetblue', 'powderblue', 'cornflowerblue', 'deepskyblue', 'dodgerblue',
  'mediumblue', 'blueviolet', 'darkorchid', 'darkviolet', 'mediumpurple',
  'mediumorchid', 'mediumvioletred', 'palevioletred', 'deeppink', 'hotpink',
  'lightcoral', 'indianred', 'rosybrown', 'sandybrown', 'goldenrod',
  'darkgoldenrod', 'burlywood', 'bisque', 'navajowhite', 'papayawhip',
  'moccasin', 'oldlace', 'floralwhite', 'ghostwhite', 'aliceblue',
  'antiquewhite', 'mistyrose', 'lavenderblush', 'lemonchiffon', 'cornsilk',
];

/**
 * Semantic Colors for feedback and status
 */
export const SEMANTIC_COLORS: Record<string, string> = {
  // Success
  'success': '#22C55E',
  'valid': '#22C55E',
  'positive': '#22C55E',
  'confirmed': '#22C55E',

  // Error/Danger
  'error': '#EF4444',
  'danger': '#EF4444',
  'invalid': '#EF4444',
  'negative': '#EF4444',
  'critical': '#EF4444',

  // Warning
  'warning orange': '#F59E0B',
  'warn': '#F59E0B',
  'alert orange': '#F59E0B',

  // Info
  'info blue': '#3B82F6',
  'informational': '#3B82F6',

  // Neutral
  'neutral': '#6B7280',
  'default': '#6B7280',

  // Disabled
  'disabled': '#9CA3AF',
  'inactive': '#9CA3AF',
};

/**
 * Phase 2.1: Named font weight mapping (100-900)
 * Maps weight names to CSS numeric values
 */
export const FONT_WEIGHT_MAP: Record<string, string> = {
  'hairline': '100',
  'thin': '100',
  'extralight': '200',
  'ultralight': '200',
  'light': '300',
  'normal': '400',
  'regular': '400',
  'medium': '500',
  'semibold': '600',
  'demibold': '600',
  'bold': '700',
  'extrabold': '800',
  'ultrabold': '800',
  'heavy': '900',
  'black': '900',
};

/**
 * Phase 2.2: Extended font family mapping (25+ fonts)
 * Includes system fonts, generic stacks, web-safe fonts, and web fonts
 */
export const FONT_FAMILY_MAP: Record<string, string> = {
  // System fonts
  'system': 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
  'system-ui': 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',

  // Generic stacks
  'serif': 'Georgia, Cambria, "Times New Roman", Times, serif',
  'sans-serif': 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  'sans': 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  'monospace': 'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Monaco, Consolas, monospace',
  'mono': 'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Monaco, Consolas, monospace',
  'code': 'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Monaco, Consolas, monospace',

  // Web-safe fonts
  'arial': 'Arial, Helvetica, sans-serif',
  'helvetica': 'Helvetica, Arial, sans-serif',
  'verdana': 'Verdana, Geneva, sans-serif',
  'tahoma': 'Tahoma, Geneva, sans-serif',
  'trebuchet': '"Trebuchet MS", Helvetica, sans-serif',
  'times': '"Times New Roman", Times, serif',
  'georgia': 'Georgia, serif',
  'palatino': '"Palatino Linotype", "Book Antiqua", Palatino, serif',
  'garamond': 'Garamond, Baskerville, serif',
  'courier': '"Courier New", Courier, monospace',
  'lucida': '"Lucida Console", Monaco, monospace',

  // Display/web fonts (require loading)
  'inter': 'Inter, system-ui, sans-serif',
  'roboto': 'Roboto, system-ui, sans-serif',
  'opensans': '"Open Sans", system-ui, sans-serif',
  'open sans': '"Open Sans", system-ui, sans-serif',
  'lato': 'Lato, system-ui, sans-serif',
  'poppins': 'Poppins, system-ui, sans-serif',
  'montserrat': 'Montserrat, system-ui, sans-serif',
  'playfair': '"Playfair Display", Georgia, serif',
  'merriweather': 'Merriweather, Georgia, serif',
};

export const DISPLAY_PRESETS: Record<string, TextStylePreset> = {
  'display-2xl': { fontSize: 72, fontWeight: '800', lineHeight: 1, letterSpacing: '-0.025em' },
  'display-xl': { fontSize: 60, fontWeight: '800', lineHeight: 1, letterSpacing: '-0.025em' },
  'display-lg': { fontSize: 48, fontWeight: '700', lineHeight: 1.1, letterSpacing: '-0.02em' },
  'display-md': { fontSize: 36, fontWeight: '700', lineHeight: 1.1, letterSpacing: '-0.02em' },
  'display-sm': { fontSize: 30, fontWeight: '600', lineHeight: 1.2, letterSpacing: '-0.01em' },
};

/**
 * Phase 2.3: Semantic text presets
 */
export const TEXT_PRESETS: Record<string, TextStylePreset> = {
  'caption': { fontSize: 12, fontWeight: '400', lineHeight: 1.4, color: '#6B7280' },
  'small': { fontSize: 12, fontWeight: '400', lineHeight: 1.4 },
  'overline': { fontSize: 12, fontWeight: '600', lineHeight: 1.4, textTransform: 'uppercase', letterSpacing: '0.05em' },
  'eyebrow': { fontSize: 12, fontWeight: '600', lineHeight: 1.4, textTransform: 'uppercase', letterSpacing: '0.05em' },
  'label': { fontSize: 14, fontWeight: '500', lineHeight: 1.4 },
  'lead': { fontSize: 20, fontWeight: '400', lineHeight: 1.6, color: '#4B5563' },
  'blockquote': { fontSize: 18, fontWeight: '400', lineHeight: 1.6, fontStyle: 'italic', color: '#4B5563' },
  'quote': { fontSize: 18, fontWeight: '400', lineHeight: 1.6, fontStyle: 'italic', color: '#4B5563' },
  'code': { fontSize: 14, fontWeight: '400', fontFamily: 'ui-monospace, SFMono-Regular, monospace' },
  'inline-code': { fontSize: 14, fontWeight: '400', fontFamily: 'ui-monospace, SFMono-Regular, monospace' },
};

/**
 * Phase 2.4: Line height presets (leading)
 */
export const LINE_HEIGHT_PRESETS: Record<string, number> = {
  'none': 1,
  'tight': 1.25,
  'snug': 1.375,
  'normal': 1.5,
  'relaxed': 1.625,
  'loose': 2,
};

/**
 * Phase 2.4: Letter spacing presets (tracking)
 */
export const LETTER_SPACING_PRESETS: Record<string, string> = {
  'tighter': '-0.05em',
  'tight': '-0.025em',
  'normal': '0',
  'wide': '0.025em',
  'wider': '0.05em',
  'widest': '0.1em',
};

/**
 * Phase 3.1: Text shadow presets
 * Various shadow intensities for text effects
 */
export const TEXT_SHADOW_PRESETS: Record<string, string> = {
  'none': 'none',
  'xs': '1px 1px 2px rgba(0, 0, 0, 0.4)',
  'sm': '1px 1px 3px rgba(0, 0, 0, 0.5)',
  'md': '2px 2px 4px rgba(0, 0, 0, 0.5)',
  'lg': '3px 3px 6px rgba(0, 0, 0, 0.5)',
  'xl': '4px 4px 8px rgba(0, 0, 0, 0.6)',
  'soft': '0 2px 8px rgba(0, 0, 0, 0.3)',
  'hard': '2px 2px 0 rgba(0, 0, 0, 0.8)',
  'crisp': '3px 3px 0 rgba(0, 0, 0, 0.9)',
};

/**
 * Phase 3.1: Glow effect presets
 * Various glow effects for text
 */
export const GLOW_PRESETS: Record<string, string> = {
  // Default glow uses cyan for visibility on both light and dark backgrounds
  'glow': '0 0 10px rgba(6, 182, 212, 0.7), 0 0 20px rgba(6, 182, 212, 0.5), 0 0 30px rgba(6, 182, 212, 0.3)',
  'soft-glow': '0 0 8px rgba(6, 182, 212, 0.4), 0 0 15px rgba(6, 182, 212, 0.2)',
  'neon': '0 0 5px currentColor, 0 0 10px currentColor, 0 0 20px currentColor',
  'blue-glow': '0 0 10px rgba(59, 130, 246, 0.5), 0 0 20px rgba(59, 130, 246, 0.3)',
  'green-glow': '0 0 10px rgba(34, 197, 94, 0.5), 0 0 20px rgba(34, 197, 94, 0.3)',
  'red-glow': '0 0 10px rgba(239, 68, 68, 0.5), 0 0 20px rgba(239, 68, 68, 0.3)',
  'purple-glow': '0 0 10px rgba(168, 85, 247, 0.5), 0 0 20px rgba(168, 85, 247, 0.3)',
  'orange-glow': '0 0 10px rgba(249, 115, 22, 0.5), 0 0 20px rgba(249, 115, 22, 0.3)',
  'cyan-glow': '0 0 10px rgba(6, 182, 212, 0.7), 0 0 20px rgba(6, 182, 212, 0.5), 0 0 30px rgba(6, 182, 212, 0.3)',
  'white-glow': '0 0 10px rgba(255, 255, 255, 0.7), 0 0 20px rgba(255, 255, 255, 0.5)',
  'yellow-glow': '0 0 10px rgba(234, 179, 8, 0.6), 0 0 20px rgba(234, 179, 8, 0.4)',
  'pink-glow': '0 0 10px rgba(236, 72, 153, 0.6), 0 0 20px rgba(236, 72, 153, 0.4)',
};

/**
 * Phase 3.1: Colored shadow mapping for common colors
 */
export const COLORED_SHADOW_MAP: Record<string, string> = {
  'red': 'rgba(239, 68, 68, 0.4)',
  'blue': 'rgba(59, 130, 246, 0.4)',
  'green': 'rgba(34, 197, 94, 0.4)',
  'purple': 'rgba(168, 85, 247, 0.4)',
  'orange': 'rgba(249, 115, 22, 0.4)',
  'yellow': 'rgba(234, 179, 8, 0.4)',
  'pink': 'rgba(236, 72, 153, 0.4)',
  'cyan': 'rgba(6, 182, 212, 0.4)',
};

/**
 * Phase 3.2: Text decoration style patterns
 */
export const DECORATION_STYLES = ['solid', 'dashed', 'dotted', 'wavy', 'double'];

/**
 * Phase 3.3: Text outline color map
 */
export const OUTLINE_COLORS: Record<string, string> = {
  'black': '#000000',
  'white': '#FFFFFF',
  'red': '#EF4444',
  'blue': '#3B82F6',
  'green': '#22C55E',
  'purple': '#A855F7',
  'orange': '#F97316',
  'yellow': '#EAB308',
  'gray': '#6B7280',
  'grey': '#6B7280',
};

/**
 * Phase 4.2: Fluid Typography Presets
 * Using clamp() for responsive font sizing that scales between min and max
 * Format: clamp(min, preferred, max)
 * Preferred uses viewport width (vw) for fluid scaling
 */
export const FLUID_SIZE_PRESETS: Record<string, string> = {
  // Extra small - for fine print, captions
  'xs': 'clamp(0.75rem, 0.7rem + 0.25vw, 0.875rem)',     // ~12px to ~14px

  // Small - for secondary text, labels
  'sm': 'clamp(0.875rem, 0.8rem + 0.375vw, 1rem)',       // ~14px to ~16px

  // Base - for body text
  'base': 'clamp(1rem, 0.9rem + 0.5vw, 1.125rem)',       // ~16px to ~18px

  // Large - for emphasized text, lead paragraphs
  'lg': 'clamp(1.125rem, 1rem + 0.625vw, 1.25rem)',      // ~18px to ~20px

  // Extra large - for section headings
  'xl': 'clamp(1.25rem, 1rem + 1.25vw, 1.5rem)',         // ~20px to ~24px

  // 2X large - for major headings
  '2xl': 'clamp(1.5rem, 1.25rem + 1.25vw, 1.875rem)',    // ~24px to ~30px

  // 3X large - for page titles
  '3xl': 'clamp(1.875rem, 1.5rem + 1.875vw, 2.25rem)',   // ~30px to ~36px

  // 4X large - for hero text
  '4xl': 'clamp(2.25rem, 1.75rem + 2.5vw, 3rem)',        // ~36px to ~48px

  // 5X large - for display text
  '5xl': 'clamp(3rem, 2rem + 5vw, 4rem)',                // ~48px to ~64px
};

/**
 * Typography scale for "one step larger/smaller" commands
 * Based on common typographic scale ratios
 */
export const TYPOGRAPHY_SCALE = [8, 10, 12, 14, 16, 18, 20, 24, 28, 32, 36, 42, 48, 56, 64, 72];
